'use client';

/**
 * BootScreen — écran de chargement des pages carte.
 *
 * Branché sur de VRAIS signaux (carte prête, requêtes arrivées) : aucun
 * pourcentage simulé. `booted` est monotone — le loader couvre dès le premier
 * paint et ne revient jamais, ce qui évite le flash « carte puis loader ».
 * Garde-fou 40 s : il ne sert qu'au cas « le réseau ne répondra jamais » (démo
 * hors connexion), jamais à écourter une attente réelle — la barre ne doit pas
 * atteindre 100 % avant que TOUS les signaux soient arrivés.
 *
 * Extrait de app/particulier/page.tsx, où le même écran vit encore en ligne :
 * il y sera branché ici lors d'un passage sur ce fichier.
 */

import { useEffect, useRef, useState } from 'react';
import { OttoIcon } from '@/components/OttoChat';
import { peekBootPct, clearBootPct } from '@/lib/bootPct';

/**
 * L'écran lui-même, sans logique. Les écrans traversés AVANT lui au lancement
 * (verrou de montage d'AppShell, sauts de zone) affichent exactement ce cadre :
 * le lancement se lit alors comme un seul écran de chargement, au lieu de la
 * séquence robot → noir → robot → barre signalée sur device.
 */
export function BootFrame({ pct, label, fade = false }: { pct: number; label: string; fade?: boolean }) {
  return (
    <div className={`fixed inset-0 z-[9999] bg-[var(--s-0)] flex flex-col items-center justify-center gap-5 transition-opacity duration-[450ms] ${fade ? 'opacity-0' : 'opacity-100'}`}>
      <span className="w-16 h-16 rounded-2xl bg-emerald-500/12 border border-emerald-500/35 text-[var(--accent-ink)] flex items-center justify-center">
        <OttoIcon size={38} />
      </span>
      <div className="w-52">
        <div className="h-1.5 rounded-full bg-[var(--s-2)] overflow-hidden">
          <div className="h-full rounded-full bg-emerald-500" style={{ width: `${pct}%` }} />
        </div>
        <p className="text-center text-xs text-[var(--ink-3)] mt-3">
          {pct >= 99 ? 'Prêt' : `${label}…`} {Math.round(pct)} %
        </p>
      </div>
    </div>
  );
}

export default function BootScreen({ signals }: { signals: [string, boolean][] }) {
  const readyCount = signals.filter(([, ok]) => ok).length;
  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setTimedOut(true), 40_000);
    return () => clearTimeout(t);
  }, []);
  const ready = readyCount === signals.length || timedOut;

  const [booted, setBooted] = useState(false);
  // Palier hérité d'AppShell/HardRedirect (rechargement dur juste avant), lu
  // dans l'initialiseur paresseux plutôt qu'un effet après coup : ce composant
  // ne monte JAMAIS en SSR (le verrou `mounted` d'AppShell rend son propre
  // cadre tant qu'il n'est pas monté côté client, `children` n'est jamais
  // rendu serveur), donc aucun risque de désaccord hydratation/SSR — lire le
  // cookie directement au premier rendu élimine le creux 16→6→16 constaté à
  // chaque relais AppShell→page (deux tentatives sessionStorage puis
  // useLayoutEffect n'avaient réglé que le SSR d'AppShell, pas ce relais-ci).
  const [pct, setPct] = useState(() => Math.max(6, peekBootPct()));
  const target = ready ? 100 : Math.min(92, 6 + readyCount * (86 / signals.length));
  // La cible passe par une ref : la boucle rAF ne doit pas être relancée à
  // chaque signal qui arrive, elle la relit simplement au tick suivant.
  const targetRef = useRef(target);
  useEffect(() => { targetRef.current = target; }, [target]);

  useEffect(() => {
    if (booted) return;
    let raf = 0;
    const tick = () => {
      setPct(p => { const t = targetRef.current; return p < t ? Math.min(t, p + Math.max(0.4, (t - p) * 0.09)) : p; });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [booted]);

  // Dérivé plutôt que stocké : l'écran s'efface dès que la barre est pleine, et
  // le démontage attend la fin du fondu.
  const fade = ready && pct >= 99.4;
  useEffect(() => {
    if (!fade || booted) return;
    clearBootPct(); // boot terminé : les lancements suivants repartent de 6 %
    const t = setTimeout(() => setBooted(true), 450);
    return () => clearTimeout(t);
  }, [fade, booted]);

  // Retour d'onglet / PWA (BFCache) : le loader recouvre à nouveau au lieu de
  // laisser apparaître une interface périmée le temps du rafraîchissement.
  useEffect(() => {
    const onShow = (e: PageTransitionEvent) => {
      if (e.persisted) { setBooted(false); setPct(6); setTimedOut(false); }
    };
    window.addEventListener('pageshow', onShow);
    return () => window.removeEventListener('pageshow', onShow);
  }, []);

  if (booted) return null;
  const label = signals.find(([, ok]) => !ok)?.[0] ?? 'Prêt';

  return <BootFrame pct={pct} label={label} fade={fade} />;
}
